import styled from "styled-components";
import { memo } from "react";

import { BreadcrumbsCommon } from "@components";
import { breakpoint } from "@utilities";
import { IBreadcrumbs } from "@interfaces";

interface IPoint {
    active: boolean;
}

interface IProcessOrderHeaderProps {
    stepLabels: string[];
    activeStepIndex: number;
    breadcrumbs: IBreadcrumbs[];
    // allow changing step
    changeStep: (newStepIndex: number) => void;
}

export const ProcessOrderHeader = memo((props: IProcessOrderHeaderProps) => {
    const { breadcrumbs, stepLabels, activeStepIndex, changeStep } = props;

    return (
        <StyledContainer>
            <div className="main">
                <div className="breadcrumbs">
                    <BreadcrumbsCommon data={breadcrumbs} />
                </div>
                {stepLabels.map((stepLabel, stepLabelIndex) => {
                    // mark this point "active" if it's at or before the current step
                    const isActiveOrBefore = stepLabelIndex <= activeStepIndex;

                    return (
                        <StyledPoint key={stepLabelIndex} active={isActiveOrBefore}>
                            <button
                                className="number"
                                disabled={stepLabelIndex === activeStepIndex}
                                style={{
                                    cursor:
                                        stepLabelIndex === activeStepIndex
                                            ? "auto"
                                            : stepLabelIndex > activeStepIndex
                                              ? "not-allowed"
                                              : "pointer",
                                }}
                                onClick={() => {
                                    // only allow going back
                                    if (stepLabelIndex < activeStepIndex) {
                                        changeStep(stepLabelIndex);
                                    }
                                }}
                            >
                                {stepLabelIndex + 1}
                                <span className="title">{stepLabel}</span>
                            </button>
                            {stepLabelIndex !== stepLabels.length - 1 && <div className="line" />}
                        </StyledPoint>
                    );
                })}
            </div>
        </StyledContainer>
    );
});

const StyledContainer = styled.div`
    ${breakpoint.breakTablet`
        background-color: #F2F2F2;
    `}

    .main {
        display: flex;
        justify-content: center;
        margin: 50px auto;
        position: relative;

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
            margin: 90px auto;
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}

        .breadcrumbs {
            position: absolute;
            left: 0;
            top: -40px;

            ${breakpoint.breakOnlyMobile`
                left: 16px;
            `}
        }
    }
`;

const StyledPoint = styled.div<IPoint>`
    display: flex;
    align-items: center;

    .number {
        cursor: pointer;
        position: relative;
        width: 24px;
        height: 24px;
        text-align: center;
        background-color: ${(props) =>
            props.active ? props.theme.colors.main : props.theme.colors.gray_4};
        border-radius: 50%;
        color: #fff;
        font-weight: 700;
        font-size: 14px;
        line-height: 24px;
        margin: 0 4px;
    }

    .line {
        width: 60px;
        border-bottom: 1px dashed #bdbdbd;

        ${breakpoint.breakTablet`
            width: 133px;
        `}
    }

    .title {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        color: ${(props) => (props.active ? props.theme.colors.main : props.theme.colors.gray_4)};
        font-weight: 500;
        font-size: 14px;
        white-space: nowrap;
        line-height: 22px;
        margin-top: 5px;
    }
`;
